import { Routes } from '@angular/router';
import { authenticationGuardFn } from './core/guards/authentication.guard';
import { devGuardFn } from './core/guards/dev.guard';
import { emptyRouteGuardFn } from './core/guards/emptyRoute.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [emptyRouteGuardFn],
    loadComponent: () => import('./layout/public-auth/public-auth.component').then((x) => x.PublicAuthComponent),
    children: [
      {
        path: '',
        loadChildren: () => import('./public/public-auth/public-auth.module').then((x) => x.PublicAuthModule),
      },
    ],
  },

  {
    path: 'public',
    loadComponent: () => import('./layout/public-pages/public-pages.component').then((x) => x.PublicPagesComponent),
    loadChildren: () => import('./public/public-pages/public-pages.module').then((x) => x.PublicPagesModule),
  },

  {
    path: 'styleguide',
    canActivate: [authenticationGuardFn, devGuardFn],
    loadChildren: () => import('./styleguide/styleguide.routes').then((x) => x.styleguideRoutes),
  },
  // This route is for development purpose only for sidebar, navigation, menu etc. after this should be deleted
  {
    path: 'dashboard',
    loadComponent: () => import('./layout/portal/portal.component').then((x) => x.PortalComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/dashboard/dashboard.component').then((x) => x.DashboardComponent),
      },
    ],
  },
  {
    path: '',
    canActivateChild: [authenticationGuardFn],
    loadComponent: () => import('./layout/portal/portal.component').then((x) => x.PortalComponent),
    children: [
      {
        path: 'me',
        loadChildren: () => import('./features/user-profile/user-profile.module').then((x) => x.UserProfileModule),
      },
      {
        path: 'partners',
        data: { breadcrumb: { alias: 'partners.title' } },
        loadChildren: () => import('./features/partners/partners.module').then((x) => x.PartnersModule),
      },
      {
        path: 'brands',
        data: { breadcrumb: { alias: 'brands.plural' } },
        loadChildren: () => import('./features/brands/brands.module').then((x) => x.BrandsModule),
      },
      {
        path: 'currencies',
        data: { breadcrumb: { alias: 'currencies.plural' } },
        loadChildren: () => import('./features/currencies/currencies.module').then((x) => x.CurrenciesModule),
      },
      {
        path: 'products',
        data: { breadcrumb: { alias: 'products.plural' } },
        loadChildren: () => import('./features/products/products.module').then((x) => x.ProductsModule),
      },
      {
        path: 'categories',
        data: { breadcrumb: { alias: 'categories.plural' } },
        loadChildren: () => import('./features/categories/categories.module').then((x) => x.CategoriesModule),
      },
      {
        path: 'countries',
        data: { breadcrumb: { alias: 'countries.plural' } },
        loadChildren: () => import('./features/countries/countries.module').then((x) => x.CountriesModule),
      },
      {
        path: 'warehouses',
        data: { breadcrumb: { alias: 'warehouses.plural' } },
        loadChildren: () => import('./features/warehouses/warehouses.module').then((x) => x.WarehousesModule),
      },
      {
        path: 'inventory',
        data: { breadcrumb: { alias: 'inventory.singular' } },
        loadChildren: () =>
          import('./features/inventory/stock-balances/stock-balance.module').then((x) => x.StockBalanceModule),
      },
      {
        path: 'attributions',
        data: { breadcrumb: { alias: 'attributions.plural' } },
        loadChildren: () => import('./features/attributions/attributions.module').then((x) => x.AttributionsModule),
      },

      {
        path: 'rp',
        loadChildren: () =>
          import('./features/roles-and-permissions/roles-and-permissions.module').then(
            (x) => x.RolesAndPermissionsModule,
          ),
      },
      {
        path: 'invoices',
        data: { breadcrumb: { alias: 'invoices.plural' } },
        loadChildren: () => import('./features/invoices/invoices.module').then((x) => x.InvoicesModule),
      },
      {
        path: 'quotations',
        data: { breadcrumb: { alias: 'quotations.plural' } },
        loadChildren: () => import('./features/quotations/quotations.module').then((x) => x.QuotationsModule),
      },
      {
        path: 'purchases',
        data: { breadcrumb: { alias: 'purchases.plural' } },
        loadChildren: () => import('./features/purchases/purchases.module').then((x) => x.PurchasesModule),
      },
      {
        path: 'delivery-slips',
        data: { breadcrumb: { alias: 'deliverySlips.plural' } },
        loadChildren: () =>
          import('./features/delivery-slips/delivery-slips.module').then((x) => x.DeliverySlipsModule),
      },
      {
        path: 'goods-receipts',
        data: { breadcrumb: { alias: 'goodsReceipts.plural' } },
        loadChildren: () =>
          import('./features/goods-receipts/goods-receipts.module').then((x) => x.GoodsReceiptsModule),
      },
      {
        path: 'appointments',
        data: { breadcrumb: { alias: 'appointments.plural' } },
        loadChildren: () => import('./features/appointments/appointments.module').then((x) => x.AppointmentsModule),
      },
      {
        path: 'holidays',
        data: { breadcrumb: { alias: 'holidays-base.plural' } },
        loadChildren: () => import('./features/holidays/holidays.module').then((x) => x.HolidaysModule),
      },
      {
        path: 'text-templates',
        data: { breadcrumb: { alias: 'textTemplates.plural' } },
        loadChildren: () =>
          import('./features/text-templates/text-templates.module').then((x) => x.TextTemplatesModule),
      },
      {
        path: 'bookable-resources',
        data: { breadcrumb: { alias: 'bookableResources.plural' } },
        loadChildren: () =>
          import('./features/bookable-resources/bookable-resources.module').then((x) => x.BookableResourcesModule),
      },
      {
        path: 'bookable-services',
        data: { breadcrumb: { alias: 'bookableServices.plural' } },
        loadChildren: () =>
          import('./features/bookable-services/bookable-services.module').then((x) => x.BookableServicesModule),
      },
      {
        path: 'subscription-plans',
        data: { breadcrumb: { alias: 'subscriptionPlans.plural' } },
        loadChildren: () =>
          import('./features/subscription-plans/subscription-plans.module').then((x) => x.SubscriptionPlansModule),
      },
      {
        path: 'workspace',
        data: { breadcrumb: { alias: 'workspaces.singular' } },
        loadChildren: () => import('./features/workspace/workspace.module').then((x) => x.WorkspaceModule),
      },
      {
        path: 'work-sessions',
        data: { breadcrumb: { alias: 'workSessions.plural' } },
        loadChildren: () => import('./features/work-sessions/work-sessions.module').then((x) => x.WorkSessionsModule),
      },
      {
        path: 'absence-management',
        data: { breadcrumb: { alias: 'absencesManagement.plural' } },
        loadChildren: () =>
          import('./features/absence-management/absence-management.module').then((x) => x.AbsenceManagementModule),
      },
      {
        path: 'absence-approvals',
        data: { breadcrumb: { alias: 'absences.plural' } },
        loadChildren: () =>
          import('./features/absences/absence-approvals/absence-approvals.module').then(
            (x) => x.AbsenceApprovalsModule,
          ),
      },
      {
        path: 'my-absences-requests',
        data: { breadcrumb: { alias: 'absences.plural' } },
        loadChildren: () =>
          import('./features/absences/my-absence-requests/my-absences-request.module').then(
            (x) => x.MyAbsencesRequestModule,
          ),
      },
      {
        path: 'res-currencies',
        data: { breadcrumb: { alias: 'resCurrencies.plural' } },
        loadChildren: () =>
          import('./features/res-currencies/res-currencies.module').then((x) => x.ResCurrenciesModule),
      },
      {
        path: 'payment-methods',
        data: { breadcrumb: { alias: 'paymentMethods.plural' } },
        loadChildren: () =>
          import('./features/payment-methods/payment-methods.module').then((x) => x.PaymentMethodsModule),
      },
      {
        path: 'pos',
        data: { breadcrumb: { alias: 'pos-base.singular' } },
        loadChildren: () => import('./features/pos/pos.module').then((x) => x.PosModule),
      },
      {
        path: 'pos-configs',
        data: { breadcrumb: { alias: 'posConfigs.plural' } },
        loadChildren: () => import('./features/pos/pos-configs/pos-configs.module').then((x) => x.PosConfigsModule),
      },
    ],
  },
];
